import {ExtensionsSettingsSubspaceState} from '~src/app/spaces/SharedSettingsSpace/ExtensionsSettingsSubspaceShared';
import {SettingsSection} from '~src/settings/components/common/SettingsSection';
import {componentRegistryEntry} from '~src/settings/general-settings/components/SettingsComponentRegistryFunctions';
import React = require('react');
import {
  ActivitySpinnerSize,
  ActivitySpinnerWidget,
  ButtonType,
  ButtonWidget,
  DialogPositioningBehavior, DialogType,
  InputLabelWidget,
  TextLinkWidget
} from '@tableau/widgets';

import {imgUrl} from '~common/utilities/ImgUrl';
import {FormikCheckbox} from '~commonInfra/components/FormikCheckbox';
import {openReactConfirmationDialog} from '~commonInfra/dialogs/ReactConfirmationDialog';
import {ModalInstance, ReactModalService} from '~commonInfra/dialogs/ReactModalService';
import {TabDialogWidth} from '~commonInfra/dialogs/style/TabDialogWidth';
import {HelpService} from '~commonInfra/services/HelpService';
import {Messages} from '~src/codegen/Localize';
import {IResult} from '~src/httpClient/serverApi/types/IResponse';
import {WebHttpClientWithErrorToastInstance} from '~src/httpClient/WebHttpClientWithErrorToast';
import {getSessionManagementServiceInstance} from '~src/sessionManagement/SessionManagementService';
import {
  AnExtSiteSettingRemoveActionStringBody,
  AnExtSiteSettingRemoveActionStringBodyEDFFOn,
  ConnectionTypes,
} from '~src/settings/components/AnalyticsExtensionsSettingsForSite';
import {
  IAnalyticsExtensionsSettings
} from '~src/settings/controllers/SiteSettingsForAnalyticsExtensionsCtrl';
import {
  AnalyticsExtensionsSettingsDialogReact,
  IAnalyticsExtensionsDialogProps
} from '~src/settings/general-settings/components/extensions-settings/analytics/AnalyticsExtensionsSettingsDialogReact';
import {AnalyticsExtensionsSettingsSubFormikProps} from '~src/settings/general-settings/components/extensions-settings/ExtensionsSettingsCoordinator';
import {AnalyticsExtensionsSettingsStyles} from '~src/settings/styles/AnalyticsExtensionsSettingsStyles';
import {learnMoreStyle} from '~src/settings/styles/CommonStyles';
import {
  IAnalyticsExtConnectionId,
  IAnalyticsExtConnectionSettings
} from '~src/settings/types/ISiteSettingsForSiteAdmin';
import {FeatureFlags} from '~src/ts/FeatureFlags';
import {
  GetExtsvcServerSettingsRequest,
  IGetExtsvcServerSettingsResult
} from '~src/ts/ServerApi/requests/GetExtsvcServerSettingsRequest';
import {AddAnalyticsExtConnectionSettingsRequest} from '~src/ts/ServerApi/requests/settings/extensions/analytics/AddAnalyticsExtConnectionSettingsRequest';
import {DeleteAnalyticsExtConnectionSettingsRequest} from '~src/ts/ServerApi/requests/settings/extensions/analytics/DeleteAnalyticsExtConnectionSettingsRequest';
import {
  GetAnalyticsExtConnectionSettingsRequest,
  IGetAnalyticsExtConnectionSettingsResult
} from '~src/ts/ServerApi/requests/settings/extensions/analytics/GetAnalyticsExtConnectionSettingsRequest';
import {
  GetAnalyticsExtSiteSettingsRequest,
  IGetAnalyticsExtSiteSettingsResult
} from '~src/ts/ServerApi/requests/settings/extensions/analytics/GetAnalyticsExtSiteSettingsRequest';
import {UpdateAnalyticsExtConnectionSettingsRequest} from '~src/ts/ServerApi/requests/settings/extensions/analytics/UpdateAnalyticsExtConnectionSettingsRequest';
import {Sites} from '~src/ts/siteManagement/services/resources/Sites';

const EINSTEIN_DISCOVERY_CELL_ICON = imgUrl('einstein_discovery_new_connection.svg');
const TABPY_CELL_ICON = imgUrl('tabpy_new_connection.svg');
const RSERVE_CELL_ICON = imgUrl('rserve_new_connection.svg');
const ANALYTICS_EXTENSION_API_CELL_ICON = imgUrl('analytics_extensions_api.svg');
const NO_CONNECTION_ICON = imgUrl('extension.svg');

const defaultAnalyticsConnection: IAnalyticsExtConnectionSettings = {
  connection_brief: {
    connection_type: 'SELECT',
    connection_name: ''
  },
  connection_luid: '',
  host: '',
  port: undefined,
  is_ssl_enabled: false,
  is_auth_enabled: false,
  username: '',
  password: ''
};

export function AnalyticsExtensionsSiteSection(props: AnalyticsExtensionsSettingsSubFormikProps) {
  const [namedConnections, setNamedConnections] = React.useState<IAnalyticsExtConnectionSettings[]>([]);
  const [defaultConnection, setDefaultConnection] = React.useState<IAnalyticsExtConnectionSettings>(defaultAnalyticsConnection);

  const sessionManagementService = getSessionManagementServiceInstance();
  const isOnline = sessionManagementService.isOnlineEnvironment();
  const isSingleSiteOnPremServer = sessionManagementService.isSingleSiteOnPremServer();

  function initializeConnections() {
    getAnalyticsConnections().then((result) => {
      if (result.length === 0) {
        result.push(defaultAnalyticsConnection);
      }
      result.map(connection => connection.connection_brief.connection_type = getConnectionTypeDisplay(connection));
      setDefaultConnection(result[0]);
      setNamedConnections(result);
    });
  }

  React.useEffect(() => initializeConnections(), []);

  function getDialogProps(): IAnalyticsExtensionsDialogProps {
    return {
      initialAnalyticsExtensionsSettings: {
        connectionDefault: defaultConnection,
        connectionSettingsList: namedConnections,
        enableExtSvcSite: props.settings.analyticsExtensionsSiteSettings.enabled
      },
      isOnline: isOnline,
      sendUpdateConnectionRequest: updateAnalyticsExtConnectionSettings,
      sendDeleteRequest: deleteAnExtConnection
    };
  }

  function showConnectionDialog(ctrlProps: IAnalyticsExtensionsDialogProps, connectionLUID: string) {
    let modalInstance: ModalInstance;
    const dialogProps = {
      dismiss: () => modalInstance && modalInstance.dismiss(),
      close: (result: any) => modalInstance && modalInstance.close(result),
      connectionLUID,
      ctrlProps: ctrlProps
    };

    modalInstance = ReactModalService.open({
      type: DialogType.Uber,
      dialogContent: React.createElement(AnalyticsExtensionsSettingsDialogReact, dialogProps),
      testId: 'analyticsConnectionsSettingDialog',
      showCloseButton: true,
      //dialogStyle: serverAndSiteUsersDialogStyle,
      shouldNotSetDefaultWidth: true
    });
    return modalInstance.result;
  }

  function showHybridDialog(ctrlProps: IAnalyticsExtensionsDialogProps, connectionLUID: string) {
    let modalInstance: ModalInstance;
    const dialogProps = {
      dismiss: () => modalInstance && modalInstance.dismiss(),
      close: (result: any) => modalInstance && modalInstance.close(result),
      connectionLUID,
      ctrlProps: ctrlProps,
      // if serverSettings is undefined, we are in online which means savedAccessTokensEnabled should be true
      savedAccessTokensEnabled: this.serverSettings ? this.serverSettings.savedAccessTokensEnabled : true
    };

    modalInstance = ReactModalService.open({
      type: DialogType.NormalWithTitleBar,
      title: isCreating(connectionLUID) ? Messages.analyticsExtensionsSettingsCreateConnectionDialogHeader() : Messages.analyticsExtensionsSettingsEditConnectionDialogHeader(),
      dialogContent: React.createElement(AnalyticsExtensionsSettingsDialogReact, dialogProps),
      testId: 'analyticsConnectionsSettingDialog',
      showCloseButton: true,
      //dialogStyle: serverAndSiteUsersDialogStyle,
      shouldNotSetDefaultWidth: true,
      positioningBehavior: DialogPositioningBehavior.Draggable,
      shouldNotCloseOnGlassClick: true
    });
    return modalInstance.result;
  }

  function isCreating(connectionLUID: string) {
    if (!FeatureFlags.analyticsExtensionsNamedConnections) {
      const params = defaultConnection;
      return params === undefined || params === null || params.connection_luid === '';
    } else {
      return connectionLUID === '';
    }
  }

  function sendDelete(connectionLUID: string) {
    let result = confirmDeleteConnectionDialog(connectionLUID, deleteAnExtConnection);
    let resultOfQuestionableType = result as unknown as PromiseLike<IAnalyticsExtensionsSettings[]>;
    resultOfQuestionableType.then((result: IAnalyticsExtensionsSettings[]) => {
      if (!FeatureFlags.analyticsExtensionsNamedConnections) {
        if (!result.length) {
          let brief = defaultAnalyticsConnection.connection_brief;
          brief = {
            ...brief,
            connection_type: ConnectionTypes.SELECT
          };
          let newDefault = {
            ...defaultAnalyticsConnection,
            connection_brief: brief
          };
          setDefaultConnection(newDefault);
        }
      } else {
        let namedConnectionsList = Object.assign([], namedConnections);
        namedConnectionsList.forEach((connection, index) => {
          if (connection.connection_luid === connectionLUID) {
            namedConnectionsList.splice(index, 1);
          }
        });
        if (namedConnectionsList.length === 0) {
          let brief = defaultAnalyticsConnection.connection_brief;
          brief = {
            ...brief,
            connection_type: ConnectionTypes.SELECT
          };
          namedConnectionsList.push({
            ...defaultAnalyticsConnection,
            connection_brief: brief
          });
        }
        setNamedConnections(namedConnectionsList);
      }
    });
  }

  function updateConnectionInfo(result: IAnalyticsExtConnectionSettings) {
    if (result) {
      const updateConnection = result;
      let connectionDisplay = '';
      if (updateConnection.connection_brief.connection_type === ConnectionTypes.TABPY.TAG) {
        connectionDisplay = ConnectionTypes.TABPY.DISPLAY;
      }
      if (updateConnection.connection_brief.connection_type === ConnectionTypes.RSERVE.TAG) {
        connectionDisplay = ConnectionTypes.RSERVE.DISPLAY;
      }
      if (updateConnection.connection_brief.connection_type === ConnectionTypes.EINSTEIN.TAG) {
        connectionDisplay = ConnectionTypes.EINSTEIN.DISPLAY;
      }
      if (updateConnection.connection_brief.connection_type === ConnectionTypes.GENERIC_API.TAG) {
        connectionDisplay = ConnectionTypes.GENERIC_API.DISPLAY;
      }
      updateConnection.connection_brief.connection_type = connectionDisplay;
      if (!FeatureFlags.analyticsExtensionsNamedConnections) {
        setDefaultConnection(updateConnection);
      } else {
        handleChangeAdaptorMultipleConnections(updateConnection);
      }
    }
  }

  function handleChangeAdaptorMultipleConnections(updatedConnection: IAnalyticsExtConnectionSettings) {
    let connectionList = Object.assign([], namedConnections);
    let found = false;
    connectionList.forEach((connection: IAnalyticsExtConnectionSettings, index: number) => {
      if (connection.connection_luid === updatedConnection.connection_luid || connection.connection_luid === '') {
        found = true;
        connectionList[index] = updatedConnection;
      }
    });
    if (!found) {
      connectionList.push(updatedConnection);
    }
    setNamedConnections(connectionList);
  }

  let extensionsDisplay;

  if (!FeatureFlags.analyticsExtensionsNamedConnections && !FeatureFlags.einsteinDiscoveryPredictionsAnalyticsExtension) {
    extensionsDisplay =
      <div>
        <div
          className={(FeatureFlags.anExtRedesignV20203Enabled && !FeatureFlags.einsteinDiscoveryPredictionsAnalyticsExtension) ? AnalyticsExtensionsSettingsStyles.connectionSettingsTable : AnalyticsExtensionsSettingsStyles.Hide}>
          <div className={AnalyticsExtensionsSettingsStyles.connectionRow}>
            <div className={AnalyticsExtensionsSettingsStyles.connectionHeaderCell} style={{width: '40%'}}>
              <InputLabelWidget testId="anext_connection_type_title" style={{fontWeight: 'bold'}}>
                {Messages.analyticsExtensionsSettingsConnectionType()}
              </InputLabelWidget>
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.connectionHeaderCell} style={{width: '60%'}}>
              <InputLabelWidget testId="anext_hostname_title" style={{fontWeight: 'bold'}}>
                {Messages.analyticsExtensionsSettingsHostname()}
              </InputLabelWidget>
            </div>
          </div>
        </div>
        <div
          className={(FeatureFlags.anExtRedesignV20203Enabled && !FeatureFlags.einsteinDiscoveryPredictionsAnalyticsExtension) ? AnalyticsExtensionsSettingsStyles.connectionSettingsTable : AnalyticsExtensionsSettingsStyles.Hide}>
          <div
            className={defaultConnection.connection_brief.connection_type === ConnectionTypes.SELECT ? AnalyticsExtensionsSettingsStyles.connectionRow : AnalyticsExtensionsSettingsStyles.Hide}>
            <div className={AnalyticsExtensionsSettingsStyles.emptyConnectionPrompt}>
              <InputLabelWidget testId="anext_no_connection_label">
                {Messages.analyticsExtensionsNoConnectionConfigured()}
              </InputLabelWidget>
              <TextLinkWidget testId="anext_connection_create_textlink" onClick={() => {
                showConnectionDialog(getDialogProps(), defaultConnection.connection_luid).then(
                  (result: IAnalyticsExtConnectionSettings) => {
                    updateConnectionInfo(result);
                  }
                );
              }}>
                <u style={{fontStyle: 'normal', fontWeight: 'normal', paddingLeft: '5px', paddingRight: '5px'}}>
                  {Messages.analyticsExtensionsSettingsCreateConnection()}
                </u>
              </TextLinkWidget>
            </div>
          </div>
          <div
            className={defaultConnection.connection_brief.connection_type === ConnectionTypes.SELECT ? AnalyticsExtensionsSettingsStyles.Hide : AnalyticsExtensionsSettingsStyles.connectionRow}>
            <div className={AnalyticsExtensionsSettingsStyles.connectionCell} style={{width: '40%'}}>
              <InputLabelWidget testId="anext_connection_type_value">
                {defaultConnection.connection_brief.connection_type}
              </InputLabelWidget>
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.connectionCell} style={{width: '40%'}}>
              <InputLabelWidget testId="anext_hostname_value">
                {defaultConnection.host}
              </InputLabelWidget>
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.connectionCell} style={{width: '5%'}}>
              <TextLinkWidget testId="anext_connection_edit_textlink" onClick={() => {
                showConnectionDialog(getDialogProps(), defaultConnection.connection_luid).then(
                  (result: IAnalyticsExtConnectionSettings) => {
                    updateConnectionInfo(result);
                  }
                );
              }}>
                <u style={{fontWeight: 'normal'}}>
                  {Messages.analyticsExtensionsSettingsEdit()}
                </u>
              </TextLinkWidget>
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.connectionCell} style={{width: '5%'}}>
              <TextLinkWidget testId="anext_connection_delete_textlink"
                              onClick={() => sendDelete(defaultConnection.connection_luid)}>
                <u style={{fontWeight: 'normal'}}>
                  {Messages.analyticsExtensionsSettingsDelete()}
                </u>
              </TextLinkWidget>
            </div>
          </div>
        </div>
      </div>;
  } else if (!FeatureFlags.analyticsExtensionsNamedConnections && FeatureFlags.einsteinDiscoveryPredictionsAnalyticsExtension) {
    extensionsDisplay =
      <div>
        <div
          className={AnalyticsExtensionsSettingsStyles.connectionSettingsTableEDFFOn}>
          <div className={AnalyticsExtensionsSettingsStyles.connectionRowEDFFOn}>
            <div className={AnalyticsExtensionsSettingsStyles.connectionHeaderCellEDFFOn} style={{width: '10%'}}>
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.connectionHeaderCellEDFFOn}
                 style={{width: '40%', verticalAlign: 'middle'}}>
              <InputLabelWidget testId="anext_connection_type_title_ed_on" style={{fontWeight: 'bold'}}>
                {Messages.analyticsExtensionsSettingsConnectionType()}
              </InputLabelWidget>
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.connectionHeaderCellEDFFOn}
                 style={{width: '50%', verticalAlign: 'middle'}}>
              <InputLabelWidget testId="anext_hostname_title_ed_on" style={{fontWeight: 'bold'}}>
                {Messages.analyticsExtensionsSettingsHostname()}
              </InputLabelWidget>
            </div>
          </div>
        </div>
        <div
          className={AnalyticsExtensionsSettingsStyles.connectionSettingsTableEDFFOn}>
          <div
            className={defaultConnection.connection_brief.connection_type === ConnectionTypes.SELECT ? AnalyticsExtensionsSettingsStyles.connectionRow : AnalyticsExtensionsSettingsStyles.Hide}>
            <div className={AnalyticsExtensionsSettingsStyles.noConnectionIcon}
                 style={{backgroundSize: 'contain', backgroundRepeat: 'no-repeat'}}></div>
            <div className={AnalyticsExtensionsSettingsStyles.noConnectionTextLabel} style={{textAlign: 'center'}}>
              <InputLabelWidget testId="anext_no_connection_label_ed_on"
                                className={AnalyticsExtensionsSettingsStyles.noConnectionText} style={{fontSize: 18}}>
                {Messages.analyticsExtensionsNoConnectionConfiguredEDFFOn()}
              </InputLabelWidget>
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.createNewConnectionButton}>
              <ButtonWidget
                testId="anext_connection_create_button_ed_on"
                buttonType={ButtonType.Outline}
                handleClick={() => {
                  if (FeatureFlags.analyticsExtensionsSettingsHybridDialog) {
                    showHybridDialog(getDialogProps(), defaultConnection.connection_luid).then(
                      (result: IAnalyticsExtConnectionSettings) => {
                        updateConnectionInfo(result);
                      }
                    );
                  } else {
                    showConnectionDialog(getDialogProps(), defaultConnection.connection_luid).then(
                      (result: IAnalyticsExtConnectionSettings) => {
                        updateConnectionInfo(result);
                      }
                    );
                  }
                }}>{Messages.analyticsExtensionsSettingsCreateConnectionEDFFOn()}</ButtonWidget>
            </div>
          </div>
          <div
            className={defaultConnection.connection_brief.connection_type === ConnectionTypes.SELECT ? AnalyticsExtensionsSettingsStyles.Hide : AnalyticsExtensionsSettingsStyles.connectionRowEDFFOn}>
            <div className={AnalyticsExtensionsSettingsStyles.connectionCellIconEDFFOn}
                 style={{
                   width: '10%',
                   backgroundImage: getConnectionTypeIcon(defaultConnection.connection_brief.connection_type)
                 }}>
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.connectionCellEDFFOn} style={{width: '40%'}}>
              <InputLabelWidget testId="anext_connection_type_value_ed_on" style={{color: '#1A699E'}}>
                {defaultConnection.connection_brief.connection_type}
              </InputLabelWidget>
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.connectionCellEDFFOn} style={{width: '40%'}}>
              <InputLabelWidget testId="anext_hostname_value_ed_on">
                {defaultConnection.host}
              </InputLabelWidget>
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.connectionCellEDFFOn} style={{width: '5%'}}>
              <ButtonWidget
                testId="anext_connection_edit_button_ed_on"
                buttonType={ButtonType.Icon}
                id="editConnectionBtn"
                className={AnalyticsExtensionsSettingsStyles.editConnectionButton}
                handleClick={() => {
                  if (FeatureFlags.analyticsExtensionsSettingsHybridDialog) {
                    showHybridDialog(getDialogProps(), defaultConnection.connection_luid).then(
                      (result: IAnalyticsExtConnectionSettings) => {
                        updateConnectionInfo(result);
                      }
                    );
                  } else {
                    showConnectionDialog(getDialogProps(), defaultConnection.connection_luid).then(
                      (result: IAnalyticsExtConnectionSettings) => {
                        updateConnectionInfo(result);
                      }
                    );
                  }
                }}>
              </ButtonWidget>
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.connectionCellEDFFOn}
                 style={{width: '5%', verticalAlign: 'middle'}}>
              <ButtonWidget
                testId="anext_connection_delete_button_ed_on"
                buttonType={ButtonType.Icon}
                id="deleteConnectionBtn"
                className={AnalyticsExtensionsSettingsStyles.deleteConnectionButton}
                onClick={() => sendDelete(defaultConnection.connection_luid)}>
              </ButtonWidget>
            </div>
          </div>
        </div>
      </div>;
  } else if (FeatureFlags.analyticsExtensionsNamedConnections) {
    let extensionRows = [];
    let waitingSpinner;

    if (namedConnections.length === 0) {
      waitingSpinner = (
        <div className={AnalyticsExtensionsSettingsStyles.waitingForConnectionsSpinner}>
          <ActivitySpinnerWidget
            testId="analytics-extensions-loading"
            spinnerSize={ActivitySpinnerSize.Large}
          ></ActivitySpinnerWidget>
        </div>);
    }

    for (let namedConnection of namedConnections) {
      extensionRows.push(
        <div
          key={namedConnection.connection_brief.connection_name}
          className={AnalyticsExtensionsSettingsStyles.connectionSettingsTableNCFFOn}>
          <div
            className={namedConnection.connection_brief.connection_type === ConnectionTypes.SELECT ? AnalyticsExtensionsSettingsStyles.connectionRow : AnalyticsExtensionsSettingsStyles.Hide}>
            <div className={AnalyticsExtensionsSettingsStyles.noConnectionIcon}
                 style={{backgroundSize: 'contain', backgroundRepeat: 'no-repeat'}}>
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.noConnectionTextLabel} style={{textAlign: 'center'}}>
              <InputLabelWidget testId="anext_no_connection_label_nc_on"
                                className={AnalyticsExtensionsSettingsStyles.noConnectionText}
                                style={{fontSize: 18}}>
                {Messages.analyticsExtensionsNoConnectionConfiguredEDFFOn()}
              </InputLabelWidget>
            </div>
          </div>
          <div
            className={namedConnection.connection_brief.connection_type === ConnectionTypes.SELECT ? AnalyticsExtensionsSettingsStyles.Hide : AnalyticsExtensionsSettingsStyles.connectionRowNCFFOn}>
            <div className={AnalyticsExtensionsSettingsStyles.connectionCellIconNCFFOn}
                 style={{
                   width: '10%',
                   backgroundImage: getConnectionTypeIcon(namedConnection.connection_brief.connection_type)
                 }}>
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.connectionCellNCFFOn} style={{width: '40%'}}>
              <InputLabelWidget testId="anext_connection_name_value_nc_on">
                {namedConnection.connection_brief.connection_name}
              </InputLabelWidget>
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.connectionCellNCFFOn} style={{width: '40%'}}>
              <InputLabelWidget testId="anext_connection_type_value_nc_on" style={{color: '#1A699E'}}>
                {namedConnection.connection_brief.connection_type}
              </InputLabelWidget>
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.connectionCellNCFFOn} style={{width: '5%'}}>
              <ButtonWidget
                testId={`anext_connection_delete_button_nc_on + ${namedConnection.connection_luid}`}
                buttonType={ButtonType.Icon}
                id={`editConnectionButton + ${namedConnection.connection_luid}`}
                className={AnalyticsExtensionsSettingsStyles.editConnectionButton}
                handleClick={() => {
                  if (FeatureFlags.analyticsExtensionsSettingsHybridDialog) {
                    showHybridDialog(getDialogProps(), namedConnection.connection_luid).then(
                      (result: IAnalyticsExtConnectionSettings) => {
                        updateConnectionInfo(result);
                      }
                    );
                  } else {
                    showConnectionDialog(getDialogProps(), namedConnection.connection_luid).then(
                      (result: IAnalyticsExtConnectionSettings) => {
                        updateConnectionInfo(result);
                      }
                    );
                  }
                }}>
              </ButtonWidget>
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.connectionCellNCFFOn}
                 style={{width: '5%', verticalAlign: 'middle'}}>
              <ButtonWidget
                testId={`anext_connection_delete_button_nc_on + ${namedConnection.connection_luid}`}
                buttonType={ButtonType.Icon}
                id={`deleteConnectionButton + ${namedConnection.connection_luid}`}
                className={AnalyticsExtensionsSettingsStyles.deleteConnectionButton}
                onClick={() => sendDelete(namedConnection.connection_luid)}>
              </ButtonWidget>
            </div>
          </div>
        </div>
      );
    }
    extensionsDisplay =
      <div>
        <div
          className={AnalyticsExtensionsSettingsStyles.connectionSettingsTableNCFFOn}>
          <div className={AnalyticsExtensionsSettingsStyles.connectionRowNCFFOn}>
            <div className={AnalyticsExtensionsSettingsStyles.connectionHeaderCellNCFFOn} style={{width: '10%'}}>
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.connectionHeaderCellNCFFOn}
                 style={{width: '40%', verticalAlign: 'middle'}}>
              <InputLabelWidget testId="anext_connection_name_title_nc_on" style={{fontWeight: 'bold'}}>
                {Messages.analyticsExtensionsSettingsConnectionName()}
              </InputLabelWidget>
            </div>
            <div className={AnalyticsExtensionsSettingsStyles.connectionHeaderCellNCFFOn}
                 style={{width: '50%', verticalAlign: 'middle'}}>
              <InputLabelWidget testId="anext_connection_type_title_nc_on" style={{fontWeight: 'bold'}}>
                {Messages.analyticsExtensionsSettingsConnectionType()}
              </InputLabelWidget>
            </div>
          </div>
        </div>
        {waitingSpinner ? waitingSpinner : extensionRows}
      </div>;
  }


  return (
    <SettingsSection title={Messages.analyticsExtensionsSettings_header()}
                     description={renderDescription()}>
      <div
        className={FeatureFlags.analyticsExtensionsNamedConnections ? AnalyticsExtensionsSettingsStyles.enableForSiteNamedConnectionsOn : AnalyticsExtensionsSettingsStyles.enableForSite}>
        <FormikCheckbox
          name="settings.analyticsExtensionsSiteSettings.enabled"
          testId="anext_site_enable_checkbox"
          label={Messages.analyticsExtensionsSettingsSite_checkbox()}
          disabled={!props.settings.analyticsExtensionsServerSettings?.enabled && !isSingleSiteOnPremServer}
        />
        <div
          className={(FeatureFlags.analyticsExtensionsNamedConnections) ? null : AnalyticsExtensionsSettingsStyles.Hide}>
          <ButtonWidget
            testId="anext_create_new_connection_button"
            buttonType={ButtonType.Primary}
            id="addNewConnectionBtn"
            handleClick={() => {
              if (FeatureFlags.analyticsExtensionsSettingsHybridDialog) {
                showHybridDialog(getDialogProps(), '').then(
                  (result: IAnalyticsExtConnectionSettings) => {
                    updateConnectionInfo(result);
                  }
                );
              } else {
                showConnectionDialog(getDialogProps(), '').then(
                  (result: IAnalyticsExtConnectionSettings) => {
                    updateConnectionInfo(result);
                  }
                );
              }
            }}>{Messages.analyticsExtensionsSettingsCreateConnection()}</ButtonWidget>
        </div>
      </div>
      <div>
        {extensionsDisplay}
      </div>
    </SettingsSection>
  );
}

function getConnectionTypeDisplay(initInfoConnection: IAnalyticsExtConnectionSettings) {
  let connection = ConnectionTypes.SELECT;
  if (initInfoConnection.connection_brief.connection_type === ConnectionTypes.TABPY.TAG) {
    connection = ConnectionTypes.TABPY.DISPLAY;
  }
  if (initInfoConnection.connection_brief.connection_type === ConnectionTypes.RSERVE.TAG) {
    connection = ConnectionTypes.RSERVE.DISPLAY;
  }
  if (initInfoConnection.connection_brief.connection_type === ConnectionTypes.EINSTEIN.TAG) {
    connection = ConnectionTypes.EINSTEIN.DISPLAY;
  }
  if (initInfoConnection.connection_brief.connection_type === ConnectionTypes.GENERIC_API.TAG) {
    connection = ConnectionTypes.GENERIC_API.DISPLAY;
  }
  return connection;
}

function getConnectionTypeIcon(type: String) {
  switch (type) {
    case ConnectionTypes.RSERVE.DISPLAY: {
      return RSERVE_CELL_ICON;
    }
    case ConnectionTypes.TABPY.DISPLAY: {
      return TABPY_CELL_ICON;
    }
    case ConnectionTypes.EINSTEIN.DISPLAY: {
      return EINSTEIN_DISCOVERY_CELL_ICON;
    }
    case ConnectionTypes.GENERIC_API.DISPLAY: {
      return ANALYTICS_EXTENSION_API_CELL_ICON;
    }
    default: {
      return NO_CONNECTION_ICON;
    }
  }
}

async function getAnalyticsConnections() {
  const result: IGetAnalyticsExtConnectionSettingsResult =
    await WebHttpClientWithErrorToastInstance.sendTagRequest(new GetAnalyticsExtConnectionSettingsRequest());
  return result.connectionList;
}

async function getAnalyticsSiteLevel() {
  const result: IGetAnalyticsExtSiteSettingsResult =
    await WebHttpClientWithErrorToastInstance.sendTagRequest(new GetAnalyticsExtSiteSettingsRequest());
  return result.enabled;
}

async function getAnalyticsServerLevel() {
  const result: IGetExtsvcServerSettingsResult =
    await WebHttpClientWithErrorToastInstance.sendTagRequest(new GetExtsvcServerSettingsRequest());
  return result.enabled;
}

function updateAnalyticsExtConnectionSettings(connectionSettings: IAnalyticsExtConnectionSettings) {
  // if this is the first time setting connection setting then need to call add instead of update
  if (connectionSettings.connection_luid === undefined || connectionSettings.connection_luid === '') {
    return WebHttpClientWithErrorToastInstance.sendTagRequest(new AddAnalyticsExtConnectionSettingsRequest(connectionSettings));
  } else {
    return WebHttpClientWithErrorToastInstance.sendTagRequest(new UpdateAnalyticsExtConnectionSettingsRequest(connectionSettings));
  }
}

function deleteAnExtConnection(connectionId: IAnalyticsExtConnectionId) {
  return WebHttpClientWithErrorToastInstance.sendTagRequest(new DeleteAnalyticsExtConnectionSettingsRequest(connectionId));
}

function renderDescription() {
  return (
    <div>
      {Messages.analyticsExtensionsSettingsServer_description()}
      <TextLinkWidget testId={'analytics-extensions-server-settings-description'}
                      target={'_blank'}
                      className={learnMoreStyle}
                      href={HelpService.getServerHelpUrlByCategory('analytics_extension')}>
        {Messages.learnMore()}
      </TextLinkWidget>
    </div>
  );
}

function confirmDeleteConnectionDialog(luid: string, request: (connectionId: IAnalyticsExtConnectionId) => Promise<IResult>) {
  const dialogInstance = openReactConfirmationDialog<void, IResult>({
    bodyComponent: FeatureFlags.einsteinDiscoveryPredictionsAnalyticsExtension ? AnExtSiteSettingRemoveActionStringBodyEDFFOn : AnExtSiteSettingRemoveActionStringBody,
    message: '',
    paramsForAction: undefined,
    actionName: FeatureFlags.einsteinDiscoveryPredictionsAnalyticsExtension ? Messages.analyticsExtensionsRemoveConnectionActionNameEDFFOn() : Messages.analyticsExtensionsRemoveConnectionActionName(),
    action: () => request({connection_luid: luid}),
    isActionDelete: true,
    dialogWidth: TabDialogWidth.width375px
  });
  return dialogInstance.result;
}

export const AnalyticsExtensionsSiteSectionSpec = componentRegistryEntry({
  name: 'AnalyticsExtensionsSiteSection',
  component: AnalyticsExtensionsSiteSection,
  onSubmit: async (settings: ExtensionsSettingsSubspaceState) => {
    await Promise.all([Sites.instance.updateAnalyticsExtensionsSiteSettings(settings.analyticsExtensionsSiteSettings)]);
  },
});
